import { useMappedInFloors, useReactMobileFloors } from 'hooks/useFloors'
import { useEffect, useState } from 'react'
import { compareFloors } from 'lib/utils/mappedin'
import { Organization } from 'lib/types'

type Floor = {
  id: string
  name: string
  elevation?: number
}

type Props = {
  onNext: (data: { matchedFloors: any }) => void
  goBack: () => void
  data: {
    org: Organization | null
  }
}

export default function Step2MatchFloors({ onNext, goBack, data }: Props) {
  const miFloors = useMappedInFloors(data.org?.mappedin)
  const rmFloors = useReactMobileFloors(data.org?.id || '')

  const [inBoth, setInBoth] = useState<{ mi: Floor; rm: string }[]>([])
  const [inMappedinOnly, setInMappedinOnly] = useState<Floor[]>([])
  const [inReactOnly, setInReactOnly] = useState<string[]>([])
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (miFloors.length && rmFloors.length) {
      const sortFloors = (floors: Floor[]) =>
        [...floors].sort((a, b) => {
          const aElev = a.elevation ?? 0
          const bElev = b.elevation ?? 0

          if (aElev !== bElev) return aElev - bElev
          return a.name.localeCompare(b.name)
        })

      const sortedMi = sortFloors(miFloors)
      const sortedRm = [...rmFloors].sort((a, b) => a.localeCompare(b))

      const { inBoth, inMappedinOnly, inReactOnly } = compareFloors(sortedMi, sortedRm)
      setInBoth(inBoth)
      setInMappedinOnly(inMappedinOnly)
      setInReactOnly(inReactOnly)
    }
  }, [miFloors, rmFloors])

  return (
    <div>
      <h2>Step 2: Match Floors</h2>
      <p>This is a comparison of floors from Mappedin and React Mobile:</p>

      <div style={{ marginTop: '2rem' }}>
        <h3>✅ Floors in Both</h3>
        {inBoth.length === 0 ? (
          <p style={{ color: '#999' }}>No matching floors found</p>
        ) : (
          <ul>
            {inBoth.map(({ mi, rm }) => (
              <li key={mi.id} style={{ color: 'green' }}>
                {mi.name} ↔ {rm}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ marginTop: '2rem' }}>
        <h3 style={{ color: 'red' }}>❌ Floors in Mappedin but NOT in React Mobile</h3>
        {inMappedinOnly.length === 0 ? (
          <p style={{ color: '#999' }}>None</p>
        ) : (
          <ul>
            {inMappedinOnly.map(floor => (
              <li key={floor.id} style={{ color: 'red' }}>
                {floor.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ marginTop: '2rem' }}>
        <h3 style={{ color: 'red' }}>❌ Floors in React Mobile but NOT in Mappedin</h3>
        {inReactOnly.length === 0 ? (
          <p style={{ color: '#999' }}>None</p>
        ) : (
          <ul>
            {inReactOnly.map(name => (
              <li key={name} style={{ color: 'red' }}>
                {name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ marginTop: '2rem' }}>
        <label>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Acknowledge you are continuing only with Matching floors
        </label>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <button
          onClick={goBack}
          style={{
            padding: '0.5rem 1.25rem',
            marginRight: '1rem',
            backgroundColor: '#ddd',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>

        <button
          onClick={() => onNext({ matchedFloors: inBoth })}
          disabled={!acknowledged}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: acknowledged ? '#29a096' : '#ccc',
            color: 'white',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            cursor: acknowledged ? 'pointer' : 'not-allowed',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  )
}
